import Avatar from '$store/components/ui/Avatar.tsx'
import { formatPrice } from '$store/sdk/format.ts'
import type {
  Filter,
  FilterToggle,
  FilterToggleValue,
  ProductListingPage,
} from 'apps/commerce/types.ts'
import { parseRange } from 'apps/commerce/utils/filters.ts'
import Icon from '$store/components/ui/Icon.tsx'
import Select from '$store/components/ui/Select.tsx'
import { IS_BROWSER } from '$fresh/runtime.ts'
import MobileFiltersAccordion, {
  MobileFilterRename,
  MobileFilterText,
} from '$store/islands/MobileFiltersAccordion.tsx'

interface Props {
  filters: ProductListingPage['filters']
  filtersHidden?: string[]
  filtersRename?: filterRename[]
  filtersText?: filterText[]
  orderFilters?: filterOrder[]
  isAside?: boolean
}

export interface filterRename {
  filter: string
  valueFilter?: string
  newName: string
}
export interface filterText {
  filter: string
  valueFilter: string
  textFilter: string
}

export interface filterOrder {
  filter: string
  priority: number
}

const isToggle = (filter: Filter): filter is FilterToggle =>
  filter['@type'] === 'FilterToggle'

function ValueItem({
  url,
  selected,
  label,
  text,
  textFilter,
}: FilterToggleValue & { text?: string; textFilter?: string }) {
  return (
    <a
      href={
        '?' +
        Object.entries(
          Object.fromEntries([
            ...new URL(
              'https://www.flamengo.com.br' + url
            ).searchParams.entries(),
          ])
        )
          .map(([k, v]) => `${k}=${v}`)
          .join('&')
      }
      rel="nofollow"
      class="flex items-center gap-2"
    >
      <span class="relative grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-primary-500 sm:h-4 sm:w-4">
        {selected && (
          <span class="absolute h-4 w-4 rounded-full bg-primary-500 sm:h-2 sm:w-2" />
        )}
      </span>
      <span class={`${textFilter && 'font-semibold flex flex-col'}`}>
        {text || label}
        {textFilter && <span class="font-light text-[12px]">{textFilter}</span>}
      </span>
    </a>
  )
}

function FilterValues({
  key,
  values,
  filtersRename,
  filtersText,
}: FilterToggle & {
  filtersRename?: filterRename[]
  filtersText?: filterText[]
}) {
  const flexDirection =
    key === 'tamanho' || key === 'cor' ? 'flex-row' : 'flex-col'

  return (
    <ul class={`flex flex-wrap gap-2 ${flexDirection}`}>
      {values.map((item) => {
        const { url, selected, value } = item

        const text = filtersRename?.find(
          (rename) => rename.filter === key && rename.valueFilter === value
        )?.newName
        const textFilter = filtersText?.find(
          (text) => text.filter === key && text.valueFilter === value
        )?.textFilter

        if (key === 'cor' || key === 'tamanho') {
          return (
            <a href={url}>
              <Avatar
                content={value}
                variant={selected ? 'active' : 'default'}
              />
            </a>
          )
        }

        if (key === 'price') {
          const range = parseRange(item.value)

          return (
            range && (
              <ValueItem
                {...item}
                label={`${formatPrice(range.from)} - ${formatPrice(range.to)}`}
              />
            )
          )
        }

        return <ValueItem {...item} text={text} textFilter={textFilter} />
      })}
    </ul>
  )
}

function Filters({
  filters,
  isAside,
  filtersHidden,
  filtersRename,
  filtersText,
  orderFilters,
}: Props) {
  filters = filters
    .filter((filter) => filter.label.toLowerCase() !== 'categoria')
    .reduce((acc, cur) => {
      if (cur.key === 'price') {
        const values = Array.isArray(cur.values) ? cur.values : [cur.values]

        // @ts-ignore this works
        cur.values = values.map((i) => {
          // @ts-ignore this works
          const [start, end] = i.label.split(':')

          // @ts-ignore this works
          if (!i.label.includes(':')) {
            return i
          }

          // @ts-ignore this works
          i.label = `${formatPrice(start)} - ${formatPrice(end)}`

          return i
        })
      }

      acc.push(cur)

      return acc
    }, [] as Filter[])

  if (filtersHidden) {
    filters = filters.filter((filter) => !filtersHidden.includes(filter.label))
  }

  if (filtersRename) {
    filters = filters.map((filter) => {
      const rename = filtersRename.find(
        (rename) => rename.filter === filter.key
      )

      if (rename && !rename.valueFilter) {
        filter.label = rename.newName
      }

      return filter
    })
  }

  if (orderFilters) {
    const orderFiltersKeys = orderFilters.map((order) => order.filter)
    const orderFiltersKeysNotFound = filters.filter(
      (filter) => !orderFiltersKeys.includes(filter.key)
    )
    const orderFiltersKeysFound = filters.filter((filter) =>
      orderFiltersKeys.includes(filter.key)
    )
    const orderFiltersKeysFoundSorted = orderFiltersKeysFound.sort((a, b) => {
      const orderA = orderFilters.find(
        (order) => order.filter === a.key
      )?.priority
      const orderB = orderFilters.find(
        (order) => order.filter === b.key
      )?.priority
      if (!orderA || !orderB) return 0
      return orderA - orderB
    })
    filters = [...orderFiltersKeysFoundSorted, ...orderFiltersKeysNotFound]
  }

  const toggleFilters = filters.filter(isToggle)

  return (
    <>
      {/* MOBILE – usa island com hooks */}
      <div class="w-full lg:hidden">
        <MobileFiltersAccordion
          filters={toggleFilters}
          filtersRename={filtersRename as MobileFilterRename[] | undefined}
          filtersText={filtersText as MobileFilterText[] | undefined}
        />

        {IS_BROWSER && (
          <a
            href={location.href.replace(/\?.+/, '')}
            class="w-full h-12 bg-primary-500 text-white rounded font-bold grid place-content-center mt-4"
          >
            Limpar
          </a>
        )}
      </div>

      {/* DESKTOP – mantém Select como era antes */}
      <ul class="hidden lg:flex w-full flex-col justify-center gap-x-6 lg:gap-[5px] lg:z-0">
        {toggleFilters.map((filter) => (
          <li class="flex flex-col gap-4 lg:w-full">
            <div class={isAside ? 'mb-4 space-y-1' : 'hidden'}>
              <span>{filter.label}</span>
              <FilterValues
                {...filter}
                filtersRename={filtersRename}
                filtersText={filtersText}
              />
            </div>

            <Select
              class="hidden w-full max-w-[180px] lg:max-w-full font-light text-neutral-0 lg:block lg:text-xs"
              classSelected="[&:checked+div]:bg-[#DA1884] [&:checked+div]:text-white [&:checked+div]:after:border-white [&:checked+div]:after:rotate-[225deg] [&:checked+div]:after:mt-[2px]"
              title={
                <div class="flex w-full items-center justify-between py-[10px] px-[15px] bg-white hover:bg-[#DA1884] text-[#2D2926] font-semibold text-[14px] hover:text-white after:border-[#2D2926] after:border-t-0 after:border-r-[2px] after:border-b-[2px] after:border-l-0 after:rotate-[45deg] after:p-1 hover:after:border-white">
                  {filter.label}
                </div>
              }
            >
              <Select.Options
                activeId={filter.values[0].value}
                class="!top-[calc(100%+20px)] lg:relative lg:z-0"
                subClass="
                  scroll
                  max-h-[240px] bg-white !w-fit
                  group-checked:border group-checked:border-[#BABABA80] group-checked:overflow-y-auto lg:bg-transparent lg:!w-full lg:max-h-[unset]
                "
              >
                <div class="flex flex-col gap-y-2.5 p-2.5">
                  {filter.values
                    .sort((a, b) => {
                      const textA =
                        filtersRename?.find(
                          (rename) =>
                            rename.filter === filter.key &&
                            rename.valueFilter === a.value
                        )?.newName || a.label

                      const textB =
                        filtersRename?.find(
                          (rename) =>
                            rename.filter === filter.key &&
                            rename.valueFilter === b.value
                        )?.newName || b.label

                      return textA.localeCompare(textB)
                    })
                    .map(({ value, label, selected, url }) => {
                      const text = filtersRename?.find(
                        (rename) =>
                          rename.filter === filter.key &&
                          rename.valueFilter === value
                      )?.newName
                      const textFilter = filtersText?.find(
                        (text) =>
                          text.filter === filter.key &&
                          text.valueFilter === value
                      )?.textFilter

                      return (
                        <a
                          href={
                            '?' +
                            Object.entries(
                              Object.fromEntries([
                                ...new URL(
                                  'https://www.flamengo.com.br' + url
                                ).searchParams.entries(),
                              ])
                            )
                              .map(([k, v]) => `${k}=${v}`)
                              .join('&')
                          }
                          role="option"
                          rel="nofollow"
                          id={label}
                          class={`flex items-center gap-x-2.5  font-light text-neutral-0 ${
                            !textFilter && 'whitespace-nowrap'
                          } group/option`}
                        >
                          <span class="relative grid h-5 w-5 shrink-0 place-items-center rounded-full border border-solid border-[#D9D9D9] bg-white sm:h-5 sm:w-5 overflow-hidden group-hover/option:border-primary-500 group">
                            {selected && <Icon id="CheckFilter" size={19} />}
                          </span>
                          <span
                            class={`${
                              textFilter && 'font-semibold flex flex-col'
                            }`}
                          >
                            {`${text ? text : label}`}

                            {textFilter && (
                              <span class="font-light text-[12px]">
                                {textFilter}
                              </span>
                            )}
                          </span>
                        </a>
                      )
                    })}
                </div>
              </Select.Options>
            </Select>
          </li>
        ))}
      </ul>
    </>
  )
}

export default Filters
